<template>
  <v-card height="100%" class="pa-2" tile>
    <v-btn @click="getRandomProject()"
      >Random template <v-icon>mdi-shuffle</v-icon></v-btn
    >
    <ProjectPreviewer
      class="mt-2"
      :project="project"
      :blocks="blocks"
      :resolution="'desktop'"
      :showBlockManagement="false"
      :editingBlockId="-1"
    />
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import ProjectPreviewer from "./ProjectPreviewer";
import { dividers } from "./blocks/DividerStylePopup.vue";
import { fontPairings } from "./SiteThemeFontSettings.vue";
// import undraw from "../../images/undraw.json";
import pulse from "../../images/pulse.json";
import remoteWork from "../../images/remote-work.json";
import streamLine from "../../images/streamline.json";

const imageLibrary = [...pulse, ...remoteWork, ...streamLine];

const r = (arr) => {
  return _.sample(arr);
};

const ok = () => _.random(0, 1);

const randomColor = () =>
  "#" + Math.floor(Math.random() * 16777215).toString(16);

function wc_hex_is_light(color) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substr(0, 2), 16);
  const c_g = parseInt(hex.substr(2, 2), 16);
  const c_b = parseInt(hex.substr(4, 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
}

function Block(block) {
  this.block = block;
  return this;
}

Block.prototype.style = function (newStyle) {
  this.block.config.style = {
    ...this.block.config.style,
    ...(newStyle || {}),
  };
  return this;
};

Block.prototype.withRandomDivider = function () {
  this.block.config.style = {
    ...this.block.config.style,
    dividerType: r(dividers).type,
  };
  return this;
};

Block.prototype.blockColorProp = function (prop, color) {
  this.block.config.style = {
    ...this.block.config.style,
    color: {
      ...this.block.config.style.color,
      [prop]: color,
    },
  };
  return this;
};

Block.prototype.withRandomImages = function () {
  if (
    this.block.type === "FEATURE" ||
    this.block.type === "FEATURE4" ||
    this.block.type === "FEATURE5" ||
    this.block.type === "FEATURE6" ||
    this.block.type === "FEATURE7" ||
    this.block.type === "FEATURE8"
  ) {
    this.block.config.media_url = r(imageLibrary).url;
  }

  return this;
};

Block.prototype.get = function () {
  return this.block;
};

export default {
  components: { ProjectPreviewer },
  computed: {
    ...mapState({
      blockDefinitions: (state) => state.blockDefinitions,
    }),
  },
  data() {
    return {
      project: {},
      blocks: [],
    };
  },
  methods: {
    getColorPalette() {
      const bgColor = randomColor();
      const isLightBgColor = wc_hex_is_light(bgColor);
      const buttonColor = isLightBgColor ? "#444444" : "#ffffff";
      const buttonLightColor = isLightBgColor ? "#ffffff" : "#444444";

      return {
        bgColor,
        buttons: {
          button: {
            text: buttonLightColor,
            background: buttonColor,
          },
          buttonLight: {
            text: buttonColor,
            background: buttonLightColor,
          },
        },
      };
    },
    getRandomizedBlocks(colorPalette) {
      const defs = JSON.parse(JSON.stringify(this.blockDefinitions));

      const topFeatures = defs["Feature"].blocks.filter(
        (r) => ["FEATURE6", "FEATURE7", "FEATURE9"].indexOf(r.type) > -1
      );
      const middleFeatures = defs["Feature"].blocks.filter(
        (r) => ["FEATURE6", "FEATURE7", "FEATURE9"].indexOf(r.type) == -1
      );

      const topStyle = {
        background: {
          color: colorPalette.bgColor,
          type: "BG_COLOR",
        },
      };

      const header = new Block(r(defs["Headers"].blocks))
        .style(topStyle)
        .blockColorProp("link", colorPalette.buttons.button.background)
        .get();

      const top = [new Block(_.sample(topFeatures))].map((block) =>
        block
          .blockColorProp("link", colorPalette.buttons.button.background)
          .blockColorProp("secondary", colorPalette.buttons.button.background)
          .blockColorProp("primary", colorPalette.buttons.button.background)
          .withRandomDivider()
          .style({
            ...topStyle,
            paddingBottom: 120,
          })
          .withRandomImages()
          .get()
      );
      const middle = [r(middleFeatures), r(defs["Logos"].blocks)];

      const bottom = [
        new Block(r(defs["Pricing"].blocks))
          .blockColorProp("link", "black")
          .blockColorProp("secondary", "black")
          .blockColorProp("primary", "black")
          .get(),
        ok() ? r(defs["Testimonial"].blocks) : null,
      ].filter(Boolean);

      const team = new Block(r(defs["Team"].blocks))
        .style(topStyle)
        .blockColorProp("link", colorPalette.buttons.button.background)
        .blockColorProp("secondary", colorPalette.buttons.button.background)
        .blockColorProp("primary", colorPalette.buttons.button.background)
        .get();

      const cta = new Block(
        r(defs["Call To Action"].blocks.filter((r) => r.type !== "CTA2"))
      )
        .withRandomDivider()
        .blockColorProp("link", colorPalette.buttons.button.background)
        .blockColorProp("secondary", colorPalette.buttons.button.background)
        .blockColorProp("primary", colorPalette.buttons.button.background)
        .style(topStyle)
        .get();

      return [
        header,
        ..._.shuffle(top),
        ..._.shuffle(middle),
        ..._.shuffle(bottom),
        ok() ? r(defs["F.A.Q"].blocks) : null,
        team,
        cta,
        ok() ? r(defs["Form"].blocks) : null,
        r(defs["Footers"].blocks),
      ].filter(Boolean);
    },
    getFontPair() {
      const pair = r(fontPairings);
      return {
        fontFamily1: pair.heading,
        fontFamily2: pair.sub,
      };
    },
    getRandomProject() {
      const colorPalette = this.getColorPalette();
      this.blocks = this.getRandomizedBlocks(colorPalette);
      // TODO: light?
      const buttonTheme = {
        ...colorPalette.buttons,
        buttonRadius: r([0, 1, 2, 3, 4]),
      };
      const fontPair = this.getFontPair();
      const randomTextSizeAdjust = r([0, 1, 2]);
      this.project = {
        id: 1,
        siteTheme: {
          ...buttonTheme,
          inputRadius: 6,
          textSize: {
            heading: 21 + randomTextSizeAdjust,
            subheading: 15 + randomTextSizeAdjust,
            text: 13 + randomTextSizeAdjust,
            text2: 9 + randomTextSizeAdjust,
          },
          ...fontPair,
        },
      };
      this.$emit("siteTheme-generated", this.project.siteTheme);
      this.$emit("blocks-generated", this.blocks);
    },
  },
  async created() {
    this.getRandomProject();
  },
};
</script>